import React, { useState, useEffect, useCallback, useRef, memo } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import { useForm, Controller } from 'react-hook-form';
import { useDropzone } from 'react-dropzone';
import CurrencyInput from 'react-currency-input-field';
import Alert from '../../../components/Alert/Alert';
import { AppAsyncSelect, AppSelect } from '../../../components/common/select';
import api from '../../../api';
import useModalEffect from '../../../hooks/useModalEffect';
import { getExpenseCategories } from '../../ExpenseCategory/api/expenseCategoryApi';

// ✅ MULTI-CURRENCY: Helper function untuk mendapatkan currency prefix
const getCurrencyPrefix = (currencyCode) => {
  const getCurrencySymbol = (code) => {
    switch (code) {
      case 'USD': return '$';
      case 'EUR': return '€';
      case 'GBP': return '£';
      case 'JPY': return '¥';
      case 'SGD': return 'S$';
      case 'AUD': return 'A$';
      case 'IDR': return 'Rp';
      default: return 'Rp';
    }
  };
  return getCurrencySymbol(currencyCode) + ' ';
};

const ExpenseForm = memo(({ isOpen, onClose, onSubmit, initialData, disabledClientId, disabledProjectId, presetClient = null }) => {
  // ✅ PERFORMANCE: Use React Hook Form for optimized form state management
  const { control, handleSubmit, reset, watch, setValue, setError, getValues, formState: { errors } } = useForm({
    defaultValues: {
      companyId: '',
      projectId: '',
      expenseDate: '',
      categoryId: '',
      amount: '',
      currencyCode: 'IDR',
      description: '',
      documents: []
    },
    mode: 'onBlur', // Only validate on blur, not on every keystroke
    shouldUnregister: false // Keep form values even when fields unmount
  });

  // Watch form values for effects (doesn't cause re-renders)
  const watchedClient = watch('companyId');
  
  const [projectOptions, setProjectOptions] = useState([]);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [isLoadingOptions, setIsLoadingOptions] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [alert, setAlert] = useState(null);
  const [documents, setDocuments] = useState([]); // Separate state for documents
  const [currentCurrencyCode, setCurrentCurrencyCode] = useState('IDR');
  const [currencyOptions, setCurrencyOptions] = useState([]);
  const [currencyRates, setCurrencyRates] = useState([]);
  const [currentExchangeRate, setCurrentExchangeRate] = useState(1);

  useModalEffect(isOpen);

  // clientInitialValue: pre-populate AppAsyncSelect in edit mode so the saved
  // company name appears immediately without waiting for the user to search.
  const clientInitialValue = initialData
    ? {
        value: String(initialData.companyId || ''),
        label: initialData.companyName || ''
      }
    // Create mode with a client chosen by the parent (company detail page).
    // The select loads its options on demand, so without an explicit label
    // the preselected client renders blank.
    : presetClient && presetClient.value
      ? { value: String(presetClient.value), label: presetClient.label || '' }
      : null;

  // ✅ PERFORMANCE: Memoize fetch functions to prevent recreation
  const fetchCategoryOptions = useCallback(async () => {
    try {
      const categoriesRes = await getExpenseCategories().catch(() => []);
      setCategoryOptions((categoriesRes || []).map(cat => ({
        value: cat.id.toString(),
        label: cat.name,
        name: cat.name
      })));
    } catch (error) {
      setCategoryOptions([]);
    }
  }, []);

  const fetchCurrencyOptions = useCallback(async () => {
    try {
      const [currenciesRes, ratesRes] = await Promise.all([
        api.get('/crm/currencies').catch(() => ({ data: [] })),
        api.get('/crm/currency-rates').catch(() => ({ data: [] }))
      ]);
      const currencies = currenciesRes.data || [];
      setCurrencyOptions(currencies.map(currency => ({
        value: currency.currencyCode || 'IDR',
        label: `${currency.currencyCode} - ${currency.currencyName}`,
        name: currency.currencyName,
        currencyCode: currency.currencyCode,
        id: currency.id
      })));
      setCurrencyRates(Array.isArray(ratesRes.data) ? ratesRes.data : []);
    } catch (error) {
      setCurrencyOptions([]);
    }
  }, []);

  const fetchProjectOptions = useCallback(async (companyId) => {
    if (!companyId) {
      setProjectOptions([]);
      return;
    }

    try {
      // ✅ FIX: Use project-options endpoint to only get converted projects (same as InvoiceForm)
      const res = await api.get(`/crm/leads/project-options?companyId=${companyId}`).catch(() => ({ data: [] }));

      // ✅ PERBAIKAN: Handle response dengan struktur baru dari API yang sudah dimodifikasi
      const formatted = (res.data || []).map(p => {
        // Buat label yang lebih informatif dengan project type
        let label = p.projectName || 'Unnamed Project';
        if (p.projectType) {
          label = `[${p.projectType}] ${p.projectName}`;
        }
        
        // Tambahkan informasi tambahan jika ada
        if (p.projectCode) {
          label += ` (${p.projectCode})`;
        } else if (p.proposalCode) {
          label += ` (${p.proposalCode})`;
        }
        
        // Use projectId if available (for Project type), otherwise use id
        const projectIdValue = p.projectId || p.id;
        return {
          value: projectIdValue ? projectIdValue.toString() : p.id.toString(),
          label: label,
          name: label,
          projectType: p.projectType,
          projectCode: p.projectCode,
          proposalCode: p.proposalCode,
          statusDesc: p.statusDesc,
          leadId: p.leadId,
          proposalId: p.proposalId,
          projectId: p.projectId || p.id
        };
      });

      setProjectOptions(formatted);
    } catch (error) {
      console.error('Error loading projects:', error);
      setProjectOptions([]);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      // Load category + currency options in parallel (client options now loaded on demand by AppAsyncSelect)
      setIsLoadingOptions(true);
      Promise.all([
        fetchCategoryOptions(),
        fetchCurrencyOptions()
      ]).finally(() => {
        setIsLoadingOptions(false);
      });
    }
  // eslint-disable-next-line
  }, [isOpen]);

  // ✅ PERFORMANCE: Handle edit mode - reset form with editing data
  const isInitializingRef = useRef(false);
  useEffect(() => {
    const initializeEditData = async () => {
      if (isOpen && initialData && !isInitializingRef.current) {
        isInitializingRef.current = true;

        const transformedData = {
          companyId: initialData.companyId ? initialData.companyId.toString() : '',
          projectId: (initialData.projectId || initialData.leadId) ? (initialData.projectId || initialData.leadId).toString() : '',
          expenseDate: initialData.expenseDate ? initialData.expenseDate.split('T')[0] : '',
          categoryId: initialData.categoryId ? initialData.categoryId.toString() : '',
          amount: initialData.amount ? initialData.amount.toString() : '',
          currencyCode: initialData.currencyCode || 'IDR',
          description: initialData.description || '',
          documents: []
        };

        reset(transformedData);
        const loadedCode = (transformedData.currencyCode || 'IDR').toUpperCase();
        setCurrentCurrencyCode(loadedCode);
        setCurrentExchangeRate(loadedCode === 'IDR' ? 1 : resolveExchangeRate(loadedCode));

        // Set uploaded files if editing
        if (initialData.fileName) {
          const existingFile = {
            name: initialData.fileName,
            size: 0,
            type: 'application/pdf',
            url: `/api/crm/expenses/download/${initialData.id}`,
            isExisting: true
          };
          setDocuments([existingFile]);
        }

        // Load projects and currency for the selected company
        if (transformedData.companyId) {
          await fetchProjectOptions(transformedData.companyId);
        }

        isInitializingRef.current = false;
      } else if (isOpen && !initialData && !isInitializingRef.current) {
        isInitializingRef.current = true;
        
        // Initialize with disabled values if provided
        const companyId = disabledClientId ? disabledClientId.toString() : '';
        const projectId = disabledProjectId ? disabledProjectId.toString() : '';
        
        // Reset form with the values
        const formValues = {
          companyId: companyId,
          projectId: projectId,
          expenseDate: '',
          categoryId: '',
          amount: '',
          currencyCode: 'IDR',
          description: '',
          documents: []
        };
        
        reset(formValues);
        
        // Explicitly set values to ensure they're selected in the form
        if (companyId) {
          setValue('companyId', companyId, { shouldValidate: false, shouldDirty: false });
        }
        
        setCurrentCurrencyCode('IDR');
        setDocuments([]);
        
        // If client is provided, load projects and currency for that client
        if (companyId) {
          await fetchProjectOptions(companyId);
          // After projects are loaded, ensure projectId is set
          // Note: initialData might have leadId (old) or projectId (new), handle both
          const projectIdToSet = initialData?.projectId || initialData?.leadId;
          if (projectIdToSet) {
            // Use setTimeout to ensure project options are populated in the dropdown
            setTimeout(() => {
              setValue('projectId', projectIdToSet, { shouldValidate: false, shouldDirty: false });
            }, 200);
          }
        } else {
          setProjectOptions([]);
        }
        
        isInitializingRef.current = false;
      }
    };

    initializeEditData();
  }, [isOpen, initialData?.id, disabledClientId, disabledProjectId]);

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      reset({
        companyId: '',
        projectId: '',
        expenseDate: '',
        categoryId: '',
        amount: '',
        currencyCode: 'IDR',
        description: '',
        documents: []
      });
      setDocuments([]);
      setCurrentCurrencyCode('IDR');
      isInitializingRef.current = false;
    }
  }, [isOpen, reset]);

  const showAlert = (type, message) => {
    setAlert({ type, message });
    setTimeout(() => setAlert(null), 3000);
  };

  // Resolve exchange rate from loaded currency_rates: IDR → 1, others → matching rate record
  const resolveExchangeRate = useCallback((code) => {
    if (!code || code.toUpperCase() === 'IDR') return 1;
    const today = new Date();
    const record = currencyRates.find(r => {
      const rCode = (r.currency_code || r.currencyCode || '').toUpperCase();
      const start = r.start_date || r.startDate ? new Date(r.start_date || r.startDate) : null;
      const end = r.end_date || r.endDate ? new Date(r.end_date || r.endDate) : null;
      return rCode === code.toUpperCase() && start && today >= start && (!end || today <= end);
    });
    return record ? Number(record.exchange_rate || record.exchangeRate || 1) : 1;
  }, [currencyRates]);

  // ✅ PERFORMANCE: Handle AppSelect changes (client, project, category)
  const handleSelectChange = useCallback((name, value) => {
    setValue(name, value, { shouldValidate: false }); // Update form state without validation
    
    if (name === 'companyId') {
      // fetchProjectOptions is also triggered by AppAsyncSelect's onItemSelect — kept here
      // so programmatic/RHF-driven companyId changes (e.g. edit-mode reset) still cascade.
      fetchProjectOptions(value);
      // Clear project when client changes, but only if project is not disabled
      if (!disabledProjectId) {
        setValue('projectId', '');
      }
    }

    if (name === 'currencyCode') {
      const code = (value || 'IDR').toUpperCase();
      setCurrentCurrencyCode(code);
      const rate = resolveExchangeRate(code);
      setCurrentExchangeRate(rate);
    }
  }, [setValue, fetchProjectOptions, disabledProjectId, currencyRates]);

  // ✅ PERFORMANCE: Handle text input changes - only update local state, sync on blur
  const handleTextInputChange = useCallback((e) => {
    const { name, value } = e.target;
    setValue(name, value, { shouldValidate: false }); // Update form state without validation
  }, [setValue]);

  // ✅ PERFORMANCE: Handle currency input change - only update local state
  const handleCurrencyChange = useCallback((name, value) => {
    setValue(name, value || '', { shouldValidate: false });
  }, [setValue]);

  const handleClose = () => {
    // Reset form when closing
    reset({
      companyId: '',
      projectId: '',
      expenseDate: '',
      categoryId: '',
          amount: '',
          currencyCode: 'IDR',
      description: '',
      documents: []
    });
    setDocuments([]);
    setProjectOptions([]);
    setCurrentCurrencyCode('IDR');
    isInitializingRef.current = false;
    if (onClose) onClose();
  };

  const onDrop = useCallback((acceptedFiles) => {
    // Only take the first file if multiple files are dropped
    const file = acceptedFiles[0];
    if (file) {
      const fileData = {
        name: file.name,
        size: `${(file.size / 1024).toFixed(1)} KB`,
        url: URL.createObjectURL(file),
        file
      };
      setDocuments([fileData]);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    maxFiles: 1,
    multiple: false,
    accept: {
      'application/pdf': ['.pdf']
    }
  });

  const validateForm = (data) => {
    const errors = {};
    if (!data.companyId) {
      errors.companyId = 'Client is required';
    }
    if (!data.projectId) {
      errors.projectId = 'Project is required';
    }
    if (!data.expenseDate) {
      errors.expenseDate = 'Date is required';
    }
    if (!data.categoryId) {
      errors.categoryId = 'Category is required';
    }
    if (!data.amount || parseFloat(data.amount) <= 0) {
      errors.amount = 'Amount is required and must be greater than 0';
    }
    return errors;
  };

  // ✅ PERFORMANCE: Handle form submit - prepare data and call parent onSubmit
  const onSubmitForm = useCallback(async (data) => {
    console.log('onSubmitForm called with data:', data);
    console.log('isSubmitting:', isSubmitting);
    console.log('documents:', documents);
    
    if (isSubmitting) {
      console.log('Already submitting, returning early');
      return;
    }

    const validationErrors = validateForm(data);
    console.log('Validation errors:', validationErrors);
    
    if (Object.keys(validationErrors).length > 0) {
      console.log('Validation failed, errors:', validationErrors);
      // Set errors in form state using react-hook-form's setError
      Object.keys(validationErrors).forEach(key => {
        setError(key, {
          type: 'manual',
          message: validationErrors[key]
        });
      });
      // Scroll to first error
      const firstErrorField = Object.keys(validationErrors)[0];
      const errorElement = document.querySelector(`[name="${firstErrorField}"]`);
      if (errorElement) {
        errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    try {
      setIsSubmitting(true);
      console.log('Starting submission...');

      // Filter out existing files (they're already on the server)
      const newFiles = documents.filter(file => !file.isExisting);
      console.log('New files to upload:', newFiles);

      const submittedCode = (data.currencyCode || currentCurrencyCode || 'IDR').toUpperCase();
      const formDataWithFiles = {
        ...data,
        currencyCode: submittedCode,
        exchangeRateAtCreation: submittedCode === 'IDR' ? 1 : currentExchangeRate,
        documents: newFiles
      };
      console.log('Form data with files:', formDataWithFiles);

      // Handle both promise and non-promise returns
      console.log('Calling onSubmit...');
      const result = onSubmit(formDataWithFiles);
      console.log('onSubmit result:', result);
      
      if (result && typeof result.then === 'function') {
        console.log('Awaiting promise...');
        await result;
        console.log('Promise resolved');
      }

      console.log('Submission successful, resetting form...');
      // Reset form after successful submission
      reset({
        companyId: '',
        projectId: '',
        expenseDate: '',
        categoryId: '',
        amount: '',
        currencyCode: 'IDR',
        description: '',
        documents: []
      });
      setDocuments([]);
      setCurrentCurrencyCode('IDR');
      handleClose();
    } catch (error) {
      console.error('Error submitting form:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      const errorMessage = error.response?.data?.error || 
                          error.response?.data?.message || 
                          error.message || 
                          'Failed to save expense';
      showAlert('danger', errorMessage);
    } finally {
      setIsSubmitting(false);
      console.log('Submission finished, isSubmitting set to false');
    }
  }, [isSubmitting, documents, onSubmit, reset, setValue, setError, handleClose]);

  return (
    <>
      {isOpen && ReactDOM.createPortal(<div className="modal-backdrop fade show" style={{ zIndex: 1040 }} />, document.body)}
      <div
        className={`modal modal-blur fade${isOpen ? ' show' : ''}`}
        id="modal-expense"
        style={{ display: isOpen ? 'block' : 'none' }}
        tabIndex="-1"
        role="dialog"
      >
        <div className="modal-dialog modal-lg modal-dialog-centered" role="document">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">
                {initialData ? 'Edit Expense' : 'Create Expense'}
              </h5>
              <button
                type="button"
                className="btn-close"
                onClick={onClose}
                aria-label="Close"
                disabled={isSubmitting}
              ></button>
            </div>

            {alert && (
              <div className="modal-body pb-0">
                <Alert
                  type={alert.type}
                  message={alert.message}
                  onClose={() => setAlert(null)}
                />
              </div>
            )}

            <form onSubmit={async (e) => {
              console.log('Form submit event triggered');
              e.preventDefault();
              
              // Try react-hook-form's handleSubmit first
              const result = handleSubmit(
                onSubmitForm,
                (errors) => {
                  console.log('React Hook Form validation errors:', errors);
                  console.log('Form values:', getValues());
                  console.log('Form errors state:', errors);
                  
                  // If handleSubmit fails, try calling onSubmitForm directly
                  console.log('handleSubmit blocked, trying direct call...');
                  const formValues = getValues();
                  console.log('Direct form values:', formValues);
                  onSubmitForm(formValues);
                }
              )(e);
              
              // If handleSubmit returns undefined (validation failed), 
              // the error handler above will handle it
              if (result === undefined) {
                console.log('handleSubmit returned undefined, error handler should have been called');
              }
            }}>
              <div className="modal-body">
                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <Controller
                        name="companyId"
                        control={control}
                        render={({ field }) => (
                          <AppAsyncSelect
                            name="companyId"
                            label="Client"
                            value={field.value}
                            onChange={handleSelectChange}
                            loadUrl="/v2/crm/companies/options"
                            placeholder="Search client..."
                            required={true}
                            error={errors.companyId?.message}
                            disabled={!!disabledClientId}
                            valueField="value"
                            labelField="label"
                            searchField="label,name"
                            initialValue={clientInitialValue}
                          />
                        )}
                      />
                    </div>
                    <div className="col-md-6 mb-3">
                      <Controller
                        name="projectId"
                        control={control}
                        render={({ field }) => (
                          <AppSelect
                            name="projectId"
                            label="Project"
                            value={field.value}
                            onChange={handleSelectChange}
                            options={projectOptions}
                            placeholder={!watchedClient ? "Please select a client first" : projectOptions.length === 0 ? "No projects available" : "Select project"}
                            required={true}
                            error={errors.projectId?.message}
                            disabled={!!disabledProjectId || !watchedClient}
                            valueField="value"
                            labelField="label"
                            searchField="label"
                          />
                        )}
                      />
                    </div>
                    <div className="col-md-6 mb-3">
                      <Controller
                        name="expenseDate"
                        control={control}
                        render={({ field }) => (
                          <>
                            <label className="form-label required">Date of Expense</label>
                            <input
                              type="date"
                              {...field}
                              className={`form-control ${errors.expenseDate ? 'is-invalid' : ''}`}
                            />
                            {errors.expenseDate && (
                              <div className="invalid-feedback">{errors.expenseDate.message}</div>
                            )}
                          </>
                        )}
                      />
                    </div>
                    <div className="col-md-6 mb-3">
                      <Controller
                        name="categoryId"
                        control={control}
                        render={({ field }) => (
                          <AppSelect
                            name="categoryId"
                            label="Category"
                            value={field.value}
                            onChange={handleSelectChange}
                            options={categoryOptions}
                            placeholder={isLoadingOptions ? "Loading categories..." : "Select category"}
                            required={true}
                            error={errors.categoryId?.message}
                            disabled={isLoadingOptions}
                            valueField="value"
                            labelField="label"
                            searchField="label"
                          />
                        )}
                      />
                    </div>
                    <div className="col-md-6 mb-3">
                      <Controller
                        name="currencyCode"
                        control={control}
                        render={({ field }) => (
                          <AppSelect
                            name="currencyCode"
                            label="Currency"
                            value={field.value}
                            onChange={handleSelectChange}
                            options={currencyOptions}
                            placeholder={isLoadingOptions ? 'Loading currencies...' : 'Select currency'}
                            required={true}
                            error={errors.currencyCode?.message}
                            disabled={isLoadingOptions}
                            valueField="value"
                            labelField="label"
                            searchField="label"
                          />
                        )}
                      />
                    </div>
                    <div className="col-md-6 mb-3">
                      <Controller
                        name="amount"
                        control={control}
                        render={({ field }) => (
                          <>
                            <label className="form-label required">Amount</label>
                            <CurrencyInput
                              className={`form-control ${errors.amount ? 'is-invalid' : ''}`}
                              name={field.name}
                              value={field.value}
                              onValueChange={(value) => {
                                field.onChange(value);
                                handleCurrencyChange('amount', value);
                              }}
                              placeholder="0"
                              decimalsLimit={0}
                              prefix={getCurrencyPrefix(currentCurrencyCode)}
                              allowNegativeValue={false}
                            />
                            {errors.amount && (
                              <div className="invalid-feedback">{errors.amount.message}</div>
                            )}
                          </>
                        )}
                      />
                    </div>
                    <div className="col-md-12 mb-3">
                      <Controller
                        name="description"
                        control={control}
                        render={({ field }) => (
                          <>
                            <label className="form-label">Description</label>
                            <textarea
                              {...field}
                              className="form-control"
                              placeholder="Enter expense description"
                              rows={3}
                            />
                          </>
                        )}
                      />
                    </div>
                  </div>

                  <div className="mb-3 mt-3">
                    <div className="modal-title">Attach Expense Document</div>
                    <div className="text-muted small">Upload PDF file (optional)</div>
                  </div>
                  <div
                    {...getRootProps()}
                    className={`dropzone ${isDragActive ? 'active' : ''}`}
                    style={{
                      border: '2px dashed #ccc',
                      borderRadius: '4px',
                      padding: '20px',
                      textAlign: 'center',
                      cursor: 'pointer',
                      backgroundColor: isDragActive ? '#f8f9fa' : 'white'
                    }}
                  >
                    <input {...getInputProps()} />
                    {documents.length > 0 ? (
                      <div className="uploaded-files">
                        {documents.map((file, index) => (
                          <div key={`${file.name}-${index}`} className="card mb-2">
                            <div className="card-body p-2">
                              <div className="d-flex align-items-center">
                                <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-file-type-pdf text-danger me-2" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                                  <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                                  <path d="M14 3v4a1 1 0 0 0 1 1h4" />
                                  <path d="M5 12v-7a2 2 0 0 1 2 -2h7l5 5v4" />
                                  <path d="M5 18h1.5a1.5 1.5 0 0 0 0 -3h-1.5v6" />
                                  <path d="M17 18h2" />
                                  <path d="M20 15h-3v6" />
                                  <path d="M11 15v6h1a2 2 0 0 0 2 -2v-2a2 2 0 0 0 -2 -2h-1z" />
                                </svg>
                                <div className="file-info flex-grow-1">
                                  <div className="text-truncate" style={{ fontSize: '14px', maxWidth: "300px" }}>
                                    {file.name}
                                    {file.isExisting && <span className="badge bg-blue-lt ms-2">Existing</span>}
                                  </div>
                                  <small className="text-muted">
                                    {file.isExisting ? 'Already uploaded' : file.size}
                                  </small>
                                </div>
                                {file.isExisting && file.url && (
                                  <a
                                    href={file.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="btn btn-icon btn-ghost-primary btn-sm me-1"
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    <svg xmlns="http://www.w3.org/2000/svg" className="icon" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                                      <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                                      <path d="M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2 -2v-2" />
                                      <path d="M7 11l5 5l5 -5" />
                                      <path d="M12 4l0 12" />
                                    </svg>
                                  </a>
                                )}
                                <button
                                  type="button"
                                  className="btn btn-icon btn-ghost-danger btn-sm"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setDocuments([]);
                                  }}
                                >
                                  <svg xmlns="http://www.w3.org/2000/svg" className="icon" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                                    <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                                    <path d="M18 6l-12 12" />
                                    <path d="M6 6l12 12" />
                                  </svg>
                                </button>
                              </div>
                            </div>
                          </div>
                        ))}
                        <div className="text-center mt-2">
                          <small className="text-muted">Drop file or click to select (PDF only)</small>
                        </div>
                      </div>
                    ) : (
                      <div className="d-flex flex-column align-items-center justify-content-center h-100">
                        <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-cloud-upload mb-2" width="48" height="48" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                          <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                          <path d="M7 18a4.6 4.4 0 0 1 0 -9a5 4.5 0 0 1 11 2h1a3.5 3.5 0 0 1 0 7h-1" />
                          <path d="M9 15l3 -3l3 3" />
                          <path d="M12 12l0 9" />
                        </svg>
                        <small className="text-center text-muted">
                          {isDragActive ? "Drop PDF file here..." : "Drop PDF file here or click to select"}
                        </small>
                        <small className="text-muted mt-1">PDF file only</small>
                      </div>
                    )}
                  </div>
              </div>
              <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-link link-secondary"
                    onClick={onClose}
                    disabled={isSubmitting}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary ms-auto"
                    disabled={isSubmitting}
                    onClick={(e) => {
                      console.log('Submit button clicked');
                      console.log('isSubmitting:', isSubmitting);
                      // Let the form handle submission
                    }}
                  >
                    {isSubmitting ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Saving...
                      </>
                    ) : (
                      'Save'
                    )}
                  </button>
                </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
});

ExpenseForm.displayName = 'ExpenseForm';

ExpenseForm.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  initialData: PropTypes.object,
  disabledClientId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  disabledProjectId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
};

export default ExpenseForm;
